import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ArtikelOutlinePage from './ArtikelOutlinePage'
import ArtikelResultPage from './ArtikelResultPage'
import { generateArtikelOutline, countWords } from './artikelUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../index.js'

// Artikel Agent States
const AGENT_STATES = {
  OUTLINE: 'outline',
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const ArtikelAgent = ({ query, onBack, llmService }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.OUTLINE)
  const [outline, setOutline] = useState([])
  const [generatedContent, setGeneratedContent] = useState({})
  const [academicSources, setAcademicSources] = useState([])
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  
  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingOutline = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `artikel_agent_${query}_${suffix}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedOutline = sessionStorage.getItem(getSessionKey('outline'))
        const persistedContent = sessionStorage.getItem(getSessionKey('content'))
        const persistedSources = sessionStorage.getItem(getSessionKey('sources'))
        
        if (persistedState && persistedOutline) {
          const state = JSON.parse(persistedState)
          const outlineData = JSON.parse(persistedOutline)
          
          console.log('📋 Loading persisted artikel state:', state.currentState)
          setCurrentState(state.currentState)
          setOutline(outlineData)
          setHasInitialized(true)
          hasEverGenerated.current = true
          
          if (persistedContent) {
            const contentData = JSON.parse(persistedContent)
            setGeneratedContent(contentData)
          }
          
          if (persistedSources) {
            const sourcesData = JSON.parse(persistedSources)
            setAcademicSources(sourcesData)
          }
          
          // Don't generate outline if we have persisted state
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('outline'))
          sessionStorage.removeItem(getSessionKey('content'))
          sessionStorage.removeItem(getSessionKey('sources'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query matches current query
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip outline generation
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`artikel_agent_${oldQuery}_state`)
        sessionStorage.removeItem(`artikel_agent_${oldQuery}_outline`)
        sessionStorage.removeItem(`artikel_agent_${oldQuery}_content`)
        sessionStorage.removeItem(`artikel_agent_${oldQuery}_sources`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
    }
    
    // Generate initial outline only if needed
    if (query && !hasInitialized && !isGeneratingOutline.current && !hasEverGenerated.current) {
      generateInitialOutline()
    }
  }, [query]) // Only depend on query, not llmService

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && outline.length > 0) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('outline'), JSON.stringify(outline))
        
        if (Object.keys(generatedContent).length > 0) {
          sessionStorage.setItem(getSessionKey('content'), JSON.stringify(generatedContent))
        }
        
        if (academicSources.length > 0) {
          sessionStorage.setItem(getSessionKey('sources'), JSON.stringify(academicSources))
        }
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, outline, generatedContent, academicSources, hasInitialized, query])

  const generateInitialOutline = async () => {
    if (isGeneratingOutline.current || hasEverGenerated.current) {
      console.log('⚠️ Outline generation already in progress or completed, skipping...')
      return
    }
    
    isGeneratingOutline.current = true
    
    try {
      setIsLoading(true)
      setError(null)
      console.log('🔄 Generating initial outline for:', query)
      
      const result = await generateArtikelOutline(query, llmService)
      setOutline(result.outline)
      setAcademicSources(result.academicSources)
      setHasInitialized(true)
      hasEverGenerated.current = true
      
      console.log('✅ Initial outline generated successfully')
    } catch (error) {
      console.error('❌ Error generating initial outline:', error)
      setError('Gagal membuat outline awal')
      setHasInitialized(false)
      hasEverGenerated.current = false // Allow retry on error
    } finally {
      setIsLoading(false)
      isGeneratingOutline.current = false
    }
  }

  const handleStartGeneration = async (finalOutline) => {
    // Check credits before starting generation
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.ARTIKEL)
    if (!canProceed) {
      return // Stop execution if insufficient credits
    }

    setCurrentState(AGENT_STATES.GENERATING)
    setOutline(finalOutline)
    setProgress({ current: 0, total: 1 }) // Single generation step
    setIsLoading(true)
    setError(null)
    
    try {
      console.log(`🔄 Memulai generate artikel lengkap untuk topik: ${query}`)
      console.log(`📝 Outline terdiri dari ${finalOutline.length} bagian`)
      
      // Update progress to show we're generating
      setProgress({ current: 0, total: 1 })
      
      // Generate entire article content in one call
      const fullArticleContent = await generateFullArticleContent(finalOutline, query, llmService, academicSources)
      
      // Parse the generated content into sections
      const parsedContent = parseFullArticleIntoSections(fullArticleContent, finalOutline)
      
      // Update generated content
      setGeneratedContent(parsedContent)
      
      // Update progress to completion
      setProgress({ current: 1, total: 1 })
      
      // Calculate total words
      const totalWords = Object.values(parsedContent).reduce((total, content) => {
        return total + countWords(content || '')
      }, 0)
      
      console.log(`🎉 Artikel lengkap selesai dibuat dengan ${totalWords} kata total`)
      
      setCurrentState(AGENT_STATES.COMPLETED)
      
      // Consume credits after successful completion
      try {
        const creditResult = await consumeCredits(AGENT_TYPES.ARTIKEL)
        if (creditResult.success) {
          console.log('✅ Kredit berhasil dikonsumsi untuk artikel agent')
        } else {
          console.warn('⚠️ Gagal mengkonsumsi kredit:', creditResult.error)
        }
      } catch (creditError) {
        console.error('❌ Error saat mengkonsumsi kredit:', creditError)
      }
      
      // Save to agent history after successful completion
      try {
        const saveResult = await AgentHistoryIntegration.saveArtikelResult({
          query: query,
          outline: finalOutline,
          generatedContent: parsedContent,
          academicSources: academicSources,
          progress: { current: 1, total: 1 }
        })
        
        if (saveResult.success) {
          console.log('✅ Artikel berhasil disimpan ke history:', saveResult.data)
        } else {
          console.warn('⚠️ Gagal menyimpan artikel ke history:', saveResult.error)
        }
      } catch (saveError) {
        console.error('❌ Error saat menyimpan ke history:', saveError)
        // Don't throw error here, just log it - the article generation was successful
      }
    } catch (err) {
      console.error('❌ Error dalam handleStartGeneration:', err)
      setError(err.message)
      setCurrentState(AGENT_STATES.OUTLINE)
    } finally {
      setIsLoading(false)
    }
  }

  const generateFullArticleContent = async (finalOutline, originalQuery, llmService, existingAcademicSources = []) => {
    // Prepare sources text
    let sourcesText = ''
    
    if (existingAcademicSources && existingAcademicSources.length > 0) {
      // Use up to 15 sources for comprehensive coverage
      const maxSources = Math.min(15, existingAcademicSources.length)
      const sourcesToUse = existingAcademicSources.slice(0, maxSources)
      
      if (sourcesToUse.length > 0) {
        sourcesText = sourcesToUse.map((source, index) => 
          `[${index + 1}] ${source.title}\n${source.snippet}\nURL: ${source.url}\n`
        ).join('\n')
        console.log(`✅ Menggunakan ${sourcesToUse.length} sumber untuk artikel lengkap`)
      }
    } else {
      console.log('⚠️ Tidak ada sumber tersedia, menggunakan pengetahuan umum')
    }

    // Create outline structure for the prompt
    const outlineStructure = finalOutline
      .filter(section => section.id !== 'referensi') // Exclude references from generation
      .map((section, index) => {
        const subsectionsText = section.subsections && section.subsections.length > 0 
          ? `\n   Sub-bagian: ${section.subsections.join(', ')}`
          : ''
        return `${index + 1}. ${section.title} (Target: ${section.minWords} kata)${subsectionsText}\n   Deskripsi: ${section.description || 'Konten utama bagian ini'}`
      }).join('\n')

    // Calculate total target words
    const totalTargetWords = finalOutline
      .filter(section => section.id !== 'referensi')
      .reduce((total, section) => total + (section.minWords || 0), 0)

    // Create comprehensive prompt for full article generation
    const prompt = `Tulis ARTIKEL LENGKAP untuk topik "${originalQuery}" dalam format Markdown dengan struktur outline yang telah ditentukan. JANGAN SERTAKAN KALIMAT BASA-BASI DI AWAL OUTPUT.

STRUKTUR OUTLINE YANG HARUS DIIKUTI:
${outlineStructure}

${sourcesText ? `SUMBER REFERENSI YANG HARUS DIGUNAKAN:
${sourcesText}

PASTIKAN untuk:
- Mengintegrasikan informasi dari sumber-sumber di atas ke dalam konten
- Memberikan sitasi dalam format [1], [2], dst. sesuai urutan sumber
- Menggunakan informasi dari sumber untuk memperkuat argumen dan memberikan kredibilitas
- Tidak hanya copy-paste, tapi mengolah informasi menjadi konten yang koheren

` : 'Karena tidak ada sumber khusus yang tersedia, gunakan pengetahuan umum yang akurat dan terpercaya untuk menulis konten.\n\n'}PEDOMAN WAJIB:
- LANGSUNG tulis artikel lengkap tanpa kalimat pembuka atau basa-basi
- MULAI dengan heading utama artikel: # ${originalQuery}
- IKUTI PERSIS struktur outline di atas dengan heading level yang tepat (## untuk bagian utama)
- TARGET TOTAL KATA: ${totalTargetWords} kata - PASTIKAN SESUAI TARGET!
- DISTRIBUSI kata per bagian harus sesuai dengan target yang ditentukan dalam outline
- Gunakan format Markdown dengan heading, subheading, dan formatting yang tepat
- Untuk list dan nested list, gunakan format yang benar:
  * Gunakan - untuk list utama
  * Gunakan spasi + - untuk nested list (contoh: "  - sub item")
  * Pastikan konsistensi indentasi
- WAJIB: Semua tabel HARUS dibuat dalam format Markdown yang benar dengan | dan header yang jelas
- Contoh format tabel yang benar:
  | Header 1 | Header 2 | Header 3 |
  |----------|----------|----------|
  | Data 1   | Data 2   | Data 3   |
- Tulis dengan gaya yang engaging dan mudah dibaca untuk web
- Berikan informasi yang praktis dan actionable
- Gunakan paragraf yang koheren dan terstruktur dengan baik
- Pastikan transisi yang smooth antar bagian untuk artikel yang mengalir dengan baik
- WAJIB: Jika ada data, perbandingan, atau informasi yang dapat disajikan dalam bentuk tabel, maka HARUS dibuat dalam format tabel Markdown
- Perluas setiap bagian dengan detail yang tepat sasaran dan contoh nyata
- Tambahkan tips dan best practices yang berguna
- JANGAN sertakan bagian Referensi - ini akan ditambahkan secara terpisah

CONTOH STRUKTUR OUTPUT:
# ${originalQuery}

## [Judul Bagian 1]
[Konten lengkap bagian 1 dengan target kata yang sesuai...]

## [Judul Bagian 2]
[Konten lengkap bagian 2 dengan target kata yang sesuai...]

[Dan seterusnya untuk semua bagian...]

Tulis artikel lengkap sekarang:`
    
    try {
      // Use GENIUS mode for high-quality content generation
      const response = await llmService.sendMessage(
        [{ role: 'user', content: prompt }],
        { 
          mode: MODEL_MODES.GENIUS,
          maxTokens: 8000, // Increased for full article generation
          searchType: 'web',
          stream: false,
          consumeCredits: false // Don't consume credits for content generation
        }
      )
      
      if (!response || response.trim().length === 0) {
        throw new Error(`Konten artikel kosong`)
      }
      
      const wordCount = countWords(response)
      console.log(`📝 Artikel lengkap selesai dibuat: ${wordCount} kata`)
      
      return response
    } catch (error) {
      console.error(`❌ Error generating full article:`, error)
      throw new Error(`Gagal membuat artikel lengkap. ${error.message}`)
    }
  }

  const parseFullArticleIntoSections = (fullContent, finalOutline) => {
    const parsedContent = {}
    
    // Split content by heading markers
    const sections = fullContent.split(/(?=^##\s)/m)
    
    // Handle main title (first section without ##)
    if (sections.length > 0 && !sections[0].trim().startsWith('##')) {
      sections.shift() // Remove the title part
    }
    
    // Match sections to outline
    finalOutline.forEach(outlineSection => {
      if (outlineSection.id === 'referensi') {
        // Skip references section - will be handled separately by ReferencesSection component
        parsedContent[outlineSection.id] = '' // Empty content for references
        return
      }
      
      // Find matching section in generated content
      const matchingSection = sections.find(section => {
        const sectionTitle = section.trim().split('\n')[0].replace(/^##\s*/, '')
        return sectionTitle.toLowerCase().includes(outlineSection.title.toLowerCase()) ||
               outlineSection.title.toLowerCase().includes(sectionTitle.toLowerCase())
      })
      
      if (matchingSection) {
        parsedContent[outlineSection.id] = matchingSection.trim()
        console.log(`✅ Mapped section: ${outlineSection.title} (${countWords(matchingSection)} kata)`)
      } else {
        console.warn(`⚠️ Could not find matching content for section: ${outlineSection.title}`)
        // Create placeholder content
        parsedContent[outlineSection.id] = `## ${outlineSection.title}\n\nKonten untuk bagian ini sedang diproses...`
      }
    })
    
    // If some sections weren't matched, try to distribute remaining content
    const unmatchedSections = sections.filter(section => {
      const sectionTitle = section.trim().split('\n')[0].replace(/^##\s*/, '')
      return !finalOutline.some(outlineSection => 
        outlineSection.title.toLowerCase().includes(sectionTitle.toLowerCase()) ||
        sectionTitle.toLowerCase().includes(outlineSection.title.toLowerCase())
      )
    })
    
    if (unmatchedSections.length > 0) {
      console.log(`📝 Distributing ${unmatchedSections.length} unmatched sections to outline`)
      const emptyOutlineSections = finalOutline.filter(section => 
        section.id !== 'referensi' && 
        (!parsedContent[section.id] || parsedContent[section.id].includes('sedang diproses'))
      )
      
      unmatchedSections.forEach((section, index) => {
        if (index < emptyOutlineSections.length) {
          const targetSection = emptyOutlineSections[index]
          parsedContent[targetSection.id] = section.trim()
          console.log(`✅ Assigned unmatched content to: ${targetSection.title}`)
        }
      })
    }
    
    return parsedContent
  }

  const handleBackToSearch = () => {
    // Clear session storage when going back to search
    try {
      sessionStorage.removeItem(getSessionKey('state'))
      sessionStorage.removeItem(getSessionKey('outline'))
      sessionStorage.removeItem(getSessionKey('content'))
      sessionStorage.removeItem(getSessionKey('sources'))
    } catch (error) {
      console.warn('⚠️ Failed to clear session storage:', error)
    }
    
    // Reset state and go back to search
    setCurrentState(AGENT_STATES.OUTLINE)
    setOutline([])
    setGeneratedContent({})
    setAcademicSources([])
    setProgress({ current: 0, total: 0 })
    setError(null)
    setHasInitialized(false)
    hasEverGenerated.current = false
    isGeneratingOutline.current = false
    onBack()
  }

  // Render based on current state
  return (
    <>
      {(() => {
        switch (currentState) {
          case AGENT_STATES.OUTLINE:
            return (
              <ArtikelOutlinePage
                query={query}
                outline={outline}
                onOutlineChange={setOutline}
                onStartGeneration={handleStartGeneration}
                onBack={onBack}
                llmService={llmService}
                isLoading={isLoading}
              />
            )
          
          case AGENT_STATES.GENERATING:
          case AGENT_STATES.COMPLETED:
            return (
              <ArtikelResultPage
                query={query}
                outline={outline}
                generatedContent={generatedContent}
                academicSources={academicSources}
                progress={progress}
                isLoading={isLoading}
                error={error}
                isCompleted={currentState === AGENT_STATES.COMPLETED}
                onBack={() => setCurrentState(AGENT_STATES.OUTLINE)}
                onBackToSearch={handleBackToSearch}
              />
            )
          
          default:
            return null
        }
      })()} 
      <CreditWarningModal 
        isOpen={warningModal.isOpen}
        onClose={closeWarning}
        creditsRequired={warningModal.creditsRequired}
        creditsRemaining={warningModal.creditsRemaining}
        packageName={warningModal.packageName}
        message={warningModal.message}
      />
    </>
  )
}

ArtikelAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

export default ArtikelAgent